import { Component, Output, Input, EventEmitter, SimpleChanges, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { NotificationRemindersService } from '../../../core/services/notification-reminders/notification-reminders.service';
import { AlertService } from '../../../core/services/alert/alert.service';
import { UsersService } from '../../../core/services/users/users.service';

@Component({
  selector: 'app-form-add-reminder',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CommonModule,
    TranslateModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
  ],
  templateUrl: './form-add-reminder.component.html',
  styleUrl: './form-add-reminder.component.css',
})
export class FormAddReminderComponent implements OnChanges {
  @Output() backButtonClick = new EventEmitter<boolean>();
  @Output() saved = new EventEmitter<boolean>();
  @Input() reminderData?: any;

  form: FormGroup;
  isEdit = false;
  accountUsers: any[] = [];
  selectedUsers: any[] = [];

  channelOptions = [
    { value: 'notification', label: 'notification_reminders.channel_options.notification' },
    { value: 'email', label: 'notification_reminders.channel_options.email' },
  ];

  constructor(
    private fb: FormBuilder,
    private remindersService: NotificationRemindersService,
    private alertService: AlertService,
    private usersService: UsersService,
  ) {
    this.form = this.fb.group({
      title: ['', Validators.required],
      description: [''],
      channels: [[], Validators.required],
      target_users: [[], Validators.required],
    });
  }

  ngOnInit() {
    this.loadUsers();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['reminderData'] && this.reminderData) {
      this.isEdit = true;
      this.selectedUsers = (this.reminderData.target_users || []).map((u: any) => ({
        ...u,
        fullName: u.fullName || u.name,
      }));
      this.form.patchValue({
        title: this.reminderData.title,
        description: this.reminderData.description,
        channels: this.reminderData.channels,
        target_users: this.selectedUsers.map((u: any) => u.reference),
      });
    } else {
      this.isEdit = false;
      this.selectedUsers = [];
      this.form.reset({ channels: [], target_users: [] });
    }
  }

  loadUsers() {
    this.usersService.getData().subscribe({
      next: (response: any) => {
        this.accountUsers = (response.data || []).map((u: any) => ({
          ...u,
          fullName: [u.name, u.lastname].filter(Boolean).join(' '),
        }));
      },
      error: () => {},
    });
  }

  get availableUsers(): any[] {
    const selectedRefs = this.selectedUsers.map(u => u.reference);
    return this.accountUsers.filter(u => !selectedRefs.includes(u.reference));
  }

  addUser(event: any): void {
    const ref = event.target.value;
    if (!ref) return;
    const user = this.accountUsers.find(u => u.reference === ref);
    if (user && !this.selectedUsers.find(u => u.reference === ref)) {
      this.selectedUsers = [...this.selectedUsers, user];
      this.syncTargetUsers();
    }
    event.target.value = '';
  }

  removeUser(reference: string): void {
    this.selectedUsers = this.selectedUsers.filter(u => u.reference !== reference);
    this.syncTargetUsers();
  }

  private syncTargetUsers(): void {
    this.form.patchValue({ target_users: this.selectedUsers.map(u => u.reference) });
  }

  onChannelToggle(channel: string) {
    const current: string[] = this.form.get('channels')?.value || [];
    if (current.includes(channel)) {
      this.form.patchValue({ channels: current.filter(c => c !== channel) });
    } else {
      this.form.patchValue({ channels: [...current, channel] });
    }
  }

  isChannelSelected(channel: string): boolean {
    return (this.form.get('channels')?.value || []).includes(channel);
  }

  emitBack() {
    this.form.reset({ channels: [], target_users: [] });
    this.selectedUsers = [];
    this.isEdit = false;
    this.backButtonClick.emit(true);
  }

  onSubmit() {
    if (this.form.invalid) return;

    const formValue = { ...this.form.value };

    if (this.isEdit && this.reminderData) {
      this.alertService.loading('swal_messages.please_wait');
      this.remindersService.updateReminder(this.reminderData.reference, formValue).subscribe({
        next: () => {
          this.alertService.successOk('swal_messages.notification_reminders.notification_reminder_updated').then(() => {
            this.saved.emit(true);
          });
        },
        error: (error) => {
          this.alertService.error(error.error?.errors || 'swal_messages.error');
        },
      });
    } else {
      this.alertService.loading('swal_messages.please_wait');
      this.remindersService.createReminder(formValue).subscribe({
        next: () => {
          this.alertService.successOk('swal_messages.notification_reminders.notification_reminder_created').then(() => {
            this.saved.emit(true);
          });
        },
        error: (error) => {
          this.alertService.error(error.error?.errors || 'swal_messages.error');
        },
      });
    }
  }
}
